import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Trip } from '../../shared/trip.model';
import { Carrier } from '../../../user-dashboard/shared/carrier.model';
import { TripSearchCriteria } from '../../shared/trip-search-criteria.model';
import { TripStatus } from '../../shared/trip-status';
import * as moment from 'moment/moment';
import { Moment } from 'moment';
import { TripDateCriteria } from '../../shared/trip-date-criteria';
import { TripService } from '../../shared/trip.service';
import { SortDirection } from '../../../shared/sort-direction';
import { finalize } from 'rxjs/operators';
import { LoaderService } from '../../../shared/services/loader.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { QuickFilter } from '../../shared/quick-filter';
import { TripStatusCriteria } from '../../shared/trip-status-criteria';
import { Region } from '../../../user-dashboard/shared/region.model';
import { TripSortableColumn } from '../../shared/trip-sortable-column';
import { DateService } from '../../../shared/date.service';
import { DateUtils } from '../../../shared/date-utils';

@Component({
  selector: 'app-load-tracking-bucket',
  templateUrl: './load-tracking-bucket.component.html',
  styleUrls: ['./load-tracking-bucket.component.scss']
})
export class LoadTrackingBucketComponent implements OnChanges {

  public noResults                          = true;
  public resultTab                          = false;
  public showAddressInfo                    = false;
  public showStatus                         = true;
  public resCount                            = 0;
  public filterSuccess                      = false;
  public filterError                        = false;
  public refCriteria: string;
  public vehicleCriteria: string;
  public departureBeforeCriteria                     = null;
  public departureAfterCriteria                      = null;
  public arrivalBeforeCriteria: string;
  public arrivalAfterCriteria: string;
  public includeUnknownDates               = false;
  public dateMin: string                             = moment().subtract(6, 'months').format('YYYY-MM-DD');
  public statusCriteria: TripStatusCriteria          = TripStatusCriteria.IN_PROGRESS;
  public departSearch: string                        = null;
  public arrivalSearch: string                       = null;

  public statusAll        = false;
  public statusInProgress = true;
  public statusCompleted  = false;
  public statusExpired = false;
  public statusCancelled  = false;
  public statusPlanned    = false;
  public statusLate       = true;
  public statusOnTime     = true;
  public statusLikelyLate = true;

  private searchCriteria: TripSearchCriteria;
  public QuickFilter   = QuickFilter;
  public SortDirection = SortDirection;

  protected readonly TripStatus          = TripStatus;
  private page                  = -1;
  private pageSize              = 15;
  private loading              = false;
  private firstLoad            = true;

  @Input()
  public trips: Trip[] = [];
  @Input()
  public carrier: Carrier;
  @Input()
  public region: Region;
  @Input()
  public selectedTrip: Trip;
  @Input()
  public scrolled: boolean;
  @Input()
  public displayableResults = false;
  @Output()
  public onAddToBucket: EventEmitter<Trip[]> = new EventEmitter<Trip[]>();
  @Output()
  public onClearBucket: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  public selectedTripChange: EventEmitter<Trip> = new EventEmitter<Trip>();
  @Output()
  public onTripRemoval: EventEmitter<Trip> = new EventEmitter<Trip>();
  @Input()
  public quickFilter: QuickFilter;
  @Output()
  public quickFilterChange: EventEmitter<QuickFilter> = new EventEmitter<QuickFilter>();
  @Output()
  public hideEmitter: EventEmitter<any> = new EventEmitter();
  @Output()
  public scrolledChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input()
  public sort: TripSortableColumn;
  @Input()
  public sortDirection: SortDirection;
  @Input()
  public statusSortDirection: TripStatus;
  @Output()
  public sortChange: EventEmitter<TripSortableColumn>        = new EventEmitter();
  @Output()
  public statusSortDirectionChange: EventEmitter<TripStatus> = new EventEmitter();
  @Output()
  public sortDirectionChange: EventEmitter<SortDirection>    = new EventEmitter();

  loadPage = (page: number, pageSize: number, sortColumn: TripSortableColumn, direction: SortDirection, statusOrder: TripStatus, criteria: TripSearchCriteria) =>
    this.tripService.searchWithAddresses(this.carrier, this.region, page, pageSize, sortColumn, direction, statusOrder, criteria);

  constructor(
    private tripService: TripService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    public dateService: DateService
  ) {
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.quickFilter && changes.quickFilter.currentValue !== this.QuickFilter.NONE) {
      this.setQuickFilterCriteria();
    }
    if (changes.scrolled && changes.scrolled.currentValue) {
      this.loadNextPage();
    }
    if (changes.displayResults && changes.displayResults.currentValue === false) {
      this.resultTab = false;
    } else if (changes.displayResults && changes.displayResults.currentValue === true) {
      this.resultTab = true;
    }
    if (
      (changes.sort && changes.sort.currentValue) ||
      (changes.sortDirection && changes.sortDirection.currentValue) ||
      (changes.statusSortDirection && changes.statusSortDirection.currentValue)
    ) {
      this.searchTrips();
    }
    if (changes.selectedTrip && changes.selectedTrip.previousValue === null) {
      this.resultTab = true;
      this.setTripResults(this.trips, this.trips.length)
    }
  }

  public resetCriteria(): void {
    this.quickFilter = QuickFilter.RESET
    this.quickFilterChange.emit(this.quickFilter);
  }

  public allChanged(doClearQuickFilter: boolean = true): void {
    if (doClearQuickFilter) {
      this.clearQuickFilter();
    }
    this.statusAll = !this.statusAll;
    if (this.statusAll) {
      this.statusInProgress = true;
      this.statusCompleted = true;
      this.statusPlanned = true;
      this.statusCancelled = true;
      this.statusExpired = true;
      this.statusLate = true;
      this.statusOnTime = true;
      this.statusLikelyLate = true;
      this.showStatus = false;
    } else {
      this.resetCommonStatuses();
      this.showStatus = true;
    }
  }

  private resetCommonStatuses(): void {
    this.statusInProgress = false;
    this.statusCompleted = false;
    this.statusExpired = false;
    this.statusPlanned = false;
    this.statusCancelled = false;
    this.statusLate = false;
    this.statusOnTime = false;
    this.statusLikelyLate = false;
  }

  public inProgressChanged(): void {
    this.clearQuickFilter();
    this.statusInProgress = !this.statusInProgress;
    if (!this.statusInProgress) {
      this.statusAll = false;
    } else if (this.isAllStatusSelected()) {
      this.statusAll = true;
    }
  }

  public completedChanged(): void {
    this.clearQuickFilter();
    this.statusCompleted = !this.statusCompleted;
    if (!this.statusCompleted) {
      this.statusAll = false;
    } else if (this.isAllStatusSelected()) {
      this.statusAll = true;
    }
  }
  public expiredChanged(): void {
    this.clearQuickFilter();
    this.statusExpired = !this.statusExpired;
    if (!this.statusExpired) {
      this.statusAll = false;
    } else if (this.isAllStatusSelected()) {
      this.statusAll = true;
    }
  }

  public cancelledChanged(): void {
    this.clearQuickFilter();
    this.statusCancelled = !this.statusCancelled;
    if (!this.statusCancelled) {
      this.statusAll = false;
    } else if (this.isAllStatusSelected()) {
      this.statusAll = true;
    }
  }

  public statusPlannedChanged(): void {
    this.clearQuickFilter();
    this.statusPlanned = !this.statusPlanned;
    if (!this.statusPlanned) {
      this.statusAll = false;
    } else if (this.isAllStatusSelected()) {
      this.statusAll = true;
    }
  }

  public statusLateChanged(): void {
    this.clearQuickFilter();
    this.statusLate = !this.statusLate;
    if (!this.statusLate) {
      this.statusAll = false;
    } else if (this.isAllStatusSelected()) {
      this.statusAll = true;
    }
  }

  public statusOnTimeChanged(): void {
    this.clearQuickFilter();
    this.statusOnTime = !this.statusOnTime;
    if (!this.statusOnTime) {
      this.statusAll = false;
    } else if (this.isAllStatusSelected()) {
      this.statusAll = true;
    }
  }

  public statusLikelyLateChanged(): void {
    this.clearQuickFilter();
    this.statusLikelyLate = !this.statusLikelyLate;
    if (!this.statusLikelyLate) {
      this.statusAll = false;
    } else if (this.isAllStatusSelected()) {
      this.statusAll = true;
    }
  }

  private isAllStatusSelected(): boolean {
    if (this.statusInProgress &&
      this.statusCompleted &&
      this.statusExpired &&
      this.statusPlanned &&
      this.statusCancelled &&
      this.statusLate &&
      this.statusOnTime &&
      this.statusLikelyLate) {
      return true;
    } else {
      this.showStatus = true;
      return false;
    }
  }

  public dateChanged(event: any, after: boolean, depart: boolean): void {
    if (depart) {
      if (after) {
        this.departureAfterCriteria = event;
        const afterMoment = moment(this.departureAfterCriteria)
        if (moment(this.departureBeforeCriteria).isBefore(afterMoment)) {
          this.departureBeforeCriteria = DateUtils.formatToSimpleDate(afterMoment);
        }
      } else {
        this.departureBeforeCriteria = event;
        const beforeMoment = moment(this.departureBeforeCriteria)
        if (moment(this.departureAfterCriteria).isAfter(beforeMoment)) {
          this.departureAfterCriteria = DateUtils.formatToSimpleDate(beforeMoment);
        }
      }
    } else {
      if (after) {
        this.arrivalAfterCriteria = event;
        const afterMoment = moment(this.arrivalAfterCriteria)
        if (moment(this.arrivalBeforeCriteria).isBefore(afterMoment)) {
          this.arrivalBeforeCriteria = DateUtils.formatToSimpleDate(afterMoment);
        }
      } else {
        this.arrivalBeforeCriteria = event;
        const beforeMoment = moment(this.arrivalBeforeCriteria)
        if (moment(this.arrivalAfterCriteria).isAfter(beforeMoment)) {
          this.arrivalAfterCriteria = DateUtils.formatToSimpleDate(beforeMoment);
        }
      }
    }
    this.clearQuickFilter();
  }

  public clearQuickFilter(): void {
    this.filterSuccess = false;
    this.filterError = false;
    this.quickFilter = QuickFilter.NONE;
    this.quickFilterChange.emit(this.quickFilter);
  }

  private setQuickFilterCriteria(): void {
    this.refCriteria = null;
    this.vehicleCriteria = null;
    this.departureBeforeCriteria = null;
    this.departureAfterCriteria = null;
    this.arrivalBeforeCriteria = null;
    this.arrivalAfterCriteria = null;
    this.includeUnknownDates = false;
    this.departSearch = null;
    this.arrivalSearch = null;
    this.statusAll = false;
    this.showStatus = true;
    this.resetCommonStatuses();
    let departureBeforeDate = null;
    let departureAfterDate = null;
    let arrivalBeforeDate = null;
    let arrivalAfterDate = null;
    switch (this.quickFilter) {
      case QuickFilter.NONE:
        break;
      case QuickFilter.IN_PROGRESS:
        this.statusCriteria = TripStatusCriteria.IN_PROGRESS;
        this.statusInProgress = true;
        this.statusLate = true;
        this.statusOnTime = true;
        this.statusLikelyLate = true;
        this.searchTrips();
        break;
      case QuickFilter.IN_PROGRESS_LATE:
        this.statusCriteria = TripStatusCriteria.IN_PROGRESS;
        this.statusInProgress = true;
        this.statusLate = true;
        this.searchTrips();
        break;
      case QuickFilter.COMPLETED_LATE:
        this.departureBeforeCriteria = null;
        this.departureAfterCriteria = null;
        arrivalBeforeDate = DateUtils.endOfToday();
        arrivalAfterDate = moment().add(-30, 'days').startOf('day');
        this.arrivalBeforeCriteria = DateUtils.simpleDateNow();
        this.arrivalAfterCriteria = DateUtils.formatToSimpleDate(arrivalAfterDate);
        this.statusCriteria = TripStatusCriteria.COMPLETED;
        this.statusCompleted = true;
        this.statusExpired = true;
        this.statusLate = true;
        this.searchTrips(null, null, arrivalBeforeDate, arrivalAfterDate, true);
        break;
      case QuickFilter.LIKELY_LATE:
        this.statusCriteria = TripStatusCriteria.IN_PROGRESS;
        this.statusInProgress = true;
        this.statusLikelyLate = true;
        this.searchTrips();
        break;
      case QuickFilter.STARTED_TODAY:
        departureBeforeDate = DateUtils.endOfToday();
        departureAfterDate = DateUtils.startOfToday();
        this.departureBeforeCriteria = DateUtils.formatToSimpleDate(departureBeforeDate);
        this.departureAfterCriteria = DateUtils.formatToSimpleDate(departureAfterDate);
        this.statusAll = false;
        this.allChanged(false);
        this.searchTrips(departureBeforeDate, departureAfterDate, null, null, true);
        break;
      case QuickFilter.COMPLETED_TODAY:
        this.departureBeforeCriteria = null;
        this.departureAfterCriteria = null;
        arrivalBeforeDate = DateUtils.endOfToday();
        arrivalAfterDate = DateUtils.startOfToday();
        this.arrivalBeforeCriteria = DateUtils.formatToSimpleDate(arrivalBeforeDate);
        this.arrivalAfterCriteria = DateUtils.formatToSimpleDate(arrivalAfterDate);
        this.statusCompleted = true;
        this.statusExpired = true;
        this.statusLate = true;
        this.statusOnTime = true;
        this.searchTrips(null, null, arrivalBeforeDate, arrivalAfterDate, true);
        break;
    }
  }

  public switchQuickFilter(filter: QuickFilter): void {
    if (this.quickFilter === filter) {
      this.quickFilter = QuickFilter.RESET;
      this.resetCriteria();
    } else {
      this.quickFilter = filter;
      this.setQuickFilterCriteria();
    }
    this.quickFilterChange.emit(this.quickFilter);
  }

  public searchTrips(
    departureBeforeDate: Moment = null,
    departureAfterDate: Moment = null,
    arrivalBeforeDate: Moment = null,
    arrivalAfterDate: Moment = null,
    quickFilter: boolean = false
  ): void {
    let status: TripStatus[] = [];
    if (this.statusAll || (!this.statusInProgress && !this.statusCompleted && !this.statusExpired && !this.statusPlanned && !this.statusCancelled)) {
      status = [TripStatus.IN_PROGRESS, TripStatus.COMPLETED, TripStatus.EXPIRED, TripStatus.PLANNED, TripStatus.CANCELLED];
    } else {
      if (this.statusInProgress) { status.push(TripStatus.IN_PROGRESS) }
      if (this.statusCompleted) { status.push(TripStatus.COMPLETED) }
      if (this.statusExpired) { status.push(TripStatus.EXPIRED) }
      if (this.statusPlanned) { status.push(TripStatus.PLANNED) }
      if (this.statusCancelled) { status.push(TripStatus.CANCELLED) }
    }
    moment.tz.setDefault(); // reset timezone to default value
    this.searchCriteria = {
      include_details: false,
      include_timezone: true,
      include_late: this.statusLate || !this.statusOnTime && !this.statusLikelyLate,
      include_on_time: this.statusOnTime || !this.statusLate && !this.statusLikelyLate,
      include_likely_late: this.statusLikelyLate || !this.statusOnTime && !this.statusLate,
      include_unknown_dates: this.includeUnknownDates,
      any_references: this.refCriteria,
      status: status,
      vehicle: this.vehicleCriteria,
      departure_date: this.departureBeforeCriteria || this.departureAfterCriteria
        ? TripDateCriteria.CUSTOM : TripDateCriteria.ALL,
      arrival_date: this.arrivalBeforeCriteria || this.arrivalAfterCriteria
        ? TripDateCriteria.CUSTOM : TripDateCriteria.ALL,
      before_departure_date: quickFilter
        ? departureBeforeDate?.toISOString() : DateUtils.endOfDateIsoString(this.departureBeforeCriteria),
      after_departure_date: quickFilter
        ? departureAfterDate?.toISOString() : DateUtils.startOfDateIsoString(this.departureAfterCriteria),
      before_arrival_date: quickFilter
        ? arrivalBeforeDate?.toISOString() : DateUtils.endOfDateIsoString(this.arrivalBeforeCriteria),
      after_arrival_date: quickFilter
        ? arrivalAfterDate?.toISOString() : DateUtils.startOfDateIsoString(this.arrivalAfterCriteria),
      departure: this.departSearch ? this.departSearch : null,
      arrival: this.arrivalSearch ? this.arrivalSearch : null,
      zone_id: DateUtils.getNavTimezone()
    }
    if (this.carrier || this.region) {
      this.firstLoad = true;
      this.reset();
      this.noResults = false;
      this.resultTab = true;
    }
  }

  public loadNextPage(): void {
    if (!this.loadPage) {
      return;
    }

    if (this.loading) {
      console.log('Already loading a page');
      return;
    }

    this.scrolled = false;
    this.scrolledChange.emit(this.scrolled);
    this.page = this.page + 1;

    this.loader.startLoad();
    this.loading = true;

    this.loadPage(this.page, this.pageSize, this.sort, this.sortDirection, this.statusSortDirection, this.searchCriteria)
      .pipe(finalize(() => {
        this.loader.stopLoad();
        this.loading = false;
      }))
      .subscribe(res => this.setTripResults(res.results, res.totalCount)
      , e => this.errorHandler.handleHttpError(e, 'data-table.errors.load-page'));
  }

  public setTripResults(trips: Trip[], totalCount: number = trips.length): void {
    this.trips = [...this.trips, ...trips];
    if (this.firstLoad) {
      this.noResults = totalCount === 0;
      this.filterSuccess = !this.noResults;
      this.filterError = this.noResults;
      this.resCount = totalCount;
    }
    this.onAddToBucket.emit(this.trips);
  }

  private reset(): void {
    this.page = -1;
    this.trips = [];
    this.onClearBucket.emit();
    this.loadNextPage();
  }

  public selectTrip(trip: Trip): void {
    if (trip !== this.selectedTrip) {
      this.selectedTrip = trip;
    } else {
      this.selectedTrip = null;
    }
    this.selectedTripChange.emit(this.selectedTrip);
  }

  public removeTrip(event: MouseEvent, trip: Trip): void {
    this.onTripRemoval.emit(trip);
    event.stopPropagation();
  }

  public hideBucket(): void {
    this.hideEmitter.emit();
  }

  public changeSort(event: TripSortableColumn) {
    this.sort = event;
    this.searchTrips();
    this.sortChange.emit(event);
  }

  public changeSortDirection(): void {
    if (this.sort === TripSortableColumn.STATUS) {
      switch (this.statusSortDirection) {
        case TripStatus.IN_PROGRESS:
          this.statusSortDirection = TripStatus.COMPLETED;
          break;
        case TripStatus.COMPLETED:
          this.statusSortDirection = TripStatus.EXPIRED;
          break;
          case TripStatus.EXPIRED:
          this.statusSortDirection = TripStatus.CANCELLED;
          break;
        case TripStatus.CANCELLED:
          this.statusSortDirection = TripStatus.PLANNED;
          break;
        case TripStatus.PLANNED:
          this.statusSortDirection = TripStatus.IN_PROGRESS;
          break;
      }
      this.statusSortDirectionChange.emit(this.statusSortDirection);
    } else {
      this.sortDirection = this.sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
      this.sortDirectionChange.emit(this.sortDirection);
    }
  }

  protected readonly moment = moment;
}
